import { useEffect } from 'react';

import { Flex, FloraButton, Toaster } from '@grupoboticario/flora-react';
import { ArrowRightIcon } from '@grupoboticario/flora-react-icons';
import { Link, useSearchParams } from 'react-router-dom';

import { Divider, Card, Tag, ResponsiveFloraTypography } from 'components';
import { DRAWERS, PAYMENT_METHODS, PAYMENT_STATUS, QUERYPARAMS } from 'config/environments/shared';
import { Payment } from 'domains/order';
import { PaymentType } from 'domains/payment-titles.d';
import { getFormattedPrice, getPaymentStatus, getShortDate } from 'helpers';
import { usePaymentDetail, useTracking } from 'hooks';

export const PaymentCard = ({ payment }: { payment: Payment }) => {
  const isBarCodeOrPix =
    payment.planType === PAYMENT_METHODS.BANKBILLING || payment.planType === PAYMENT_METHODS.PIX;
  const [searchParams, setSearchParams] = useSearchParams();
  const isExpired = payment.status === PAYMENT_STATUS.EXPIRED;
  const isBoleto = payment.paymentType === PaymentType.Boleto;
  const { sendDataToGTM } = useTracking();

  const paymentStatus = getPaymentStatus({
    daysLate: payment.daysLate,
    daysUntilDueDate: payment.daysUntilDueDate,
    paymentStatus: payment.paymentStatus,
    status: payment.status,
  });
  const {
    data: paymentDetail,
    isError: isErrorPaymentDetail,
    isLoading: isLoadingPaymentDetail,
    trigger: triggerPaymentDetail,
  } = usePaymentDetail(searchParams.get(QUERYPARAMS.PAYMENT_ID) || '');
  const isSetPaymentDetailDrawer: boolean =
    searchParams.get(QUERYPARAMS.PAYMENT_ID) === payment.paymentTitleId;

  const setDrawerParams = () => {
    setSearchParams({
      paymentId: payment.paymentTitleId,
      isOpen: 'true',
      drawer: DRAWERS.PAYMENT_DETAIL,
    });
  };

  const handleDrawer = () => {
    trackPaymentDetail();
    setDrawerParams();
    return triggerPaymentDetail();
  };

  const trackPaymentDetail = () => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_a_pagar',
        cd_interaction_detail: 'ver-detalhes-do-pagamento',
        transaction_id: paymentDetail?.orderId,
        cd_metodo_pagamento: isBoleto ? 'boleto' : 'pix',
        cd_status_pagamento: paymentStatus?.tag,
        cd_dias_para_vencimento: paymentDetail?.daysLate,
        cd_dias_apos_vencimento: paymentDetail?.daysUntilDueDate,
      },
    });
  };

  useEffect(() => {
    if (isSetPaymentDetailDrawer && !isExpired) {
      setDrawerParams();
      triggerPaymentDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetPaymentDetailDrawer]);

  useEffect(() => {
    if (isErrorPaymentDetail) {
      Toaster.notify({
        kind: 'error',
        description: `Não foi possível exibir os detalhes do pagamento. Por favor, tente novamente.`,
        origin: 'right-top',
      });
    }
  }, [isErrorPaymentDetail]);

  if (
    payment.paymentStatus === PAYMENT_STATUS.UNBILLED &&
    (payment.planType === PaymentType.Boleto || payment.planType === PaymentType.Pix)
  ) {
    return (
      <Card>
        <ResponsiveFloraTypography>
          Seu boleto ou código PIX está sendo gerado e estará disponível em breve.
        </ResponsiveFloraTypography>
      </Card>
    );
  }

  return (
    <Flex key={payment.amount} direction='column' gap='$4'>
      {isBarCodeOrPix && (
        <Card>
          <Flex direction='column' gap='$3'>
            <Flex align='center' justify='space-between'>
              <Flex direction='column' align='flex-start'>
                <ResponsiveFloraTypography
                  color='$backgroundAltPrimary'
                  as='h2'
                  fontSize='subtitle'
                  fontWeight='bold'
                >
                  {getFormattedPrice(payment.amount)}
                </ResponsiveFloraTypography>

                <ResponsiveFloraTypography fontSize='bodySmallStandard'>
                  {paymentStatus?.subTitle}
                </ResponsiveFloraTypography>
              </Flex>

              {paymentStatus && (
                <Tag
                  data={{
                    ...paymentStatus,
                    tagText: paymentStatus.tag,
                    subTitleText: paymentStatus.subTitle || '',
                  }}
                  type='pagamento'
                />
              )}
            </Flex>

            <Divider variant='Line' />

            <Flex align='center' justify='space-between'>
              <ResponsiveFloraTypography fontSize='bodySmallStandard'>
                Vencimento
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography fontSize='bodySmallStandard'>
                {getShortDate(payment.dueDate)}
              </ResponsiveFloraTypography>
            </Flex>

            {paymentStatus?.tag !== 'Pago' && !isExpired ? (
              <>
                <Divider variant='Line' />
                <FloraButton
                  onClick={handleDrawer}
                  icon={<ArrowRightIcon />}
                  size='small'
                  hierarchy='tertiary'
                  disabled={isLoadingPaymentDetail && isSetPaymentDetailDrawer}
                  isFull
                  isLoading={isLoadingPaymentDetail && isSetPaymentDetailDrawer}
                >
                  <ResponsiveFloraTypography fontSize='bodySmallStandard' fontWeight='bold'>
                    {isLoadingPaymentDetail && isSetPaymentDetailDrawer
                      ? 'Carregando...'
                      : 'Ver detalhes do pagamento'}
                  </ResponsiveFloraTypography>
                </FloraButton>
              </>
            ) : null}

            {isExpired ? (
              <>
                <Divider variant='Line' />
                <FloraButton
                  aria-label={`Acessar renegociação do pedido ${payment.paymentTitleId}`}
                  as={Link}
                  size='small'
                  data-id={payment.paymentTitleId}
                  isFull
                  icon={<ArrowRightIcon />}
                  has='iconRight'
                  role='button'
                  to={`?drawer=Renegotiation&isOpen=true&paymentId=${payment.paymentTitleId}`}
                  hierarchy='tertiary'
                  autotagging-flora='on'
                  data-flora='button'
                  data-flora-text={isBoleto ? 'renegociar-boleto' : 'renegociar-pagamento'}
                >
                  <ResponsiveFloraTypography fontSize='bodySmallStandard' fontWeight='bold'>
                    {isBoleto ? 'Renegociar boleto' : 'Renegociar pagamento'}
                  </ResponsiveFloraTypography>
                </FloraButton>
              </>
            ) : null}
          </Flex>
        </Card>
      )}
    </Flex>
  );
};
